import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import type { CheckReportFirmVariant } from '../../types/report';

/**
 * R17 Task H: dismissible yellow banner that surfaces raw firm-name
 * spellings that collapsed to the same canonical firm. Peter uses this
 * to spot typos and fix them on the Manual Checks page — the link at
 * the bottom of the banner deep-links there.
 */
interface Props {
  variants: CheckReportFirmVariant[];
}

const CheckReportVariantsBanner: React.FC<Props> = ({ variants }) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || variants.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-800">
            {variants.length} Firm{variants.length > 1 ? 's have' : ' has'} Multiple Name Spellings
          </h3>
          <p className="mt-1 text-xs text-amber-700">
            The checks below were grouped by normalized firm name (case + whitespace collapsed).
            Fix the raw spellings on the{' '}
            <Link to="/manual-checks" className="underline font-medium hover:text-amber-900">
              Manual Checks
            </Link>{' '}
            page so future reports group cleanly.
          </p>
          <div className="mt-3 space-y-1">
            {variants.map((v, idx) => (
              <div key={`${v.canonical}-${idx}`} className="text-sm bg-white/60 rounded px-3 py-1.5">
                <span className="font-medium text-amber-900">{v.canonical}</span>
                <span className="text-amber-700 text-xs ml-2">
                  variants: {v.variants.map(s => `"${s}"`).join(', ')}
                </span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="ml-2 text-amber-600 hover:text-amber-800 flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default CheckReportVariantsBanner;
